import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Snackbar } from '@mui/material';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { PageSurface } from './PageSurface';
import { AppErrorBoundary } from '../AppErrorBoundary';
import { useAuthStore } from '../../store/auth';
import { useWorkspace } from '../../hooks/useWorkspace';
import { api } from '../../lib/api';
import { stopImpersonation } from '../../lib/impersonation';
import {
  clearPendingBillingTier,
  getPendingBillingTier,
  isCheckoutBillingTier,
  startBillingCheckout,
} from '../../lib/billing';
import { PlanGateHost } from '../billing/PlanGateHost';
import { QuotaBanner } from '../billing/QuotaBanner';
import { SampleDataBanner } from '../billing/SampleDataBanner';
import { useCanManageBilling } from '../../hooks/useBillingAccess';
import { VERIFICATION_UNLOCKS, type FlashState } from '../../lib/verificationReturn';
import { wrappingAlertSx } from '../../lib/alertLayout';

const SIDEBAR_WIDTH = 216;


/** Shown while a page's code chunk loads; the layout stays in place. */
function PageLoading() {
  return (
    <Box display="flex" justifyContent="center" py={8} sx={{ color: 'text.secondary' }}>
      <CircularProgress color="inherit" aria-label="Loading page" />
    </Box>
  );
}

export function AppLayout() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [checkoutError, setCheckoutError] = React.useState<string | null>(null);
  const [verificationSent, setVerificationSent] = React.useState(false);
  const [stoppingImpersonation, setStoppingImpersonation] = React.useState(false);
  const [impersonationError, setImpersonationError] = React.useState(false);
  const checkoutStartedRef = React.useRef(false);
  const isEmbedded = useAuthStore((s) => s.isEmbedded);
  const user = useAuthStore((s) => s.user);
  const { workspaceId } = useWorkspace();
  const canManageBilling = useCanManageBilling();
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const [flash, setFlash] = React.useState<string | null>(null);

  // One-off success messages handed over through navigation state (e.g. after confirming an email).
  React.useEffect(() => {
    const message = (location.state as FlashState | null)?.flash;
    if (!message) return;
    setFlash(message);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);
  // A crashing page shows its fallback inside the layout (sidebar and top bar keep working);
  // navigating elsewhere clears it. App.tsx keeps a last-resort boundary around everything.
  const page = (
    <AppErrorBoundary variant="page" resetKey={pathname}>
      <React.Suspense fallback={<PageLoading />}><Outlet /></React.Suspense>
    </AppErrorBoundary>
  );

  React.useEffect(() => {
    if (isEmbedded || !workspaceId || checkoutStartedRef.current) return;
    const tier = getPendingBillingTier();
    if (!isCheckoutBillingTier(tier)) {
      clearPendingBillingTier();
      return;
    }
    checkoutStartedRef.current = true;
    clearPendingBillingTier();
    void startBillingCheckout(workspaceId, tier).catch((error: unknown) => {
      setCheckoutError(error instanceof Error ? error.message : 'Unable to start checkout.');
    });
  }, [isEmbedded, workspaceId]);

  // When embedded (white-label mode) render only the content — no sidebar or topbar.
  // No viewport-based min-height: the host sizes the frame to the content, and a
  // 100vh floor would make the frame ratchet up and never shrink.
  if (isEmbedded) {
    return (
      <Box
        sx={{
          bgcolor: 'background.default',
          color: 'text.primary',
          p: { xs: 2, md: 3 },
        }}
      >
        <PageSurface embedded>{page}</PageSurface>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Sidebar
        width={SIDEBAR_WIDTH}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${SIDEBAR_WIDTH}px)` },
          ml: { sm: `${SIDEBAR_WIDTH}px` },
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          minWidth: 0,
        }}
      >
        <TopBar onMenuClick={() => setMobileOpen(true)} />
        {user?.impersonator && (
          <Alert
            severity="warning"
            variant="filled"
            action={
              <Button
                color="inherit"
                size="small"
                disabled={stoppingImpersonation}
                onClick={() => {
                  setStoppingImpersonation(true);
                  setImpersonationError(false);
                  void stopImpersonation().catch(() => {
                    setStoppingImpersonation(false);
                    setImpersonationError(true);
                  });
                }}
              >
                {stoppingImpersonation ? 'Returning…' : 'Return to admin'}
              </Button>
            }
          >
            {impersonationError
              ? 'Unable to return to your administrator account. Sign out and sign in again.'
              : `You are impersonating ${user.name} (${user.email}). Changes you make are saved to their account.`}
          </Alert>
        )}
        {!user?.emailVerifiedAt && (
          <Alert
            severity="warning"
            sx={wrappingAlertSx}
            action={
              <Button
                color="inherit"
                size="small"
                disabled={verificationSent}
                onClick={() => {
                  void api.post('/auth/verify-email/request').then(() => setVerificationSent(true));
                }}
              >
                {verificationSent ? 'Sent' : 'Resend email'}
              </Button>
            }
          >
            Confirm your email to unlock {VERIFICATION_UNLOCKS}.{user?.email ? <> We sent a link to <strong>{user.email}</strong>.</> : null}
          </Alert>
        )}
        <QuotaBanner canManageBilling={canManageBilling} />
        <SampleDataBanner />
        <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, flexGrow: 1, minWidth: 0 }}>
          <PageSurface>{page}</PageSurface>
        </Box>
      </Box>
      <PlanGateHost />
      <Snackbar
        open={!!flash}
        autoHideDuration={8000}
        onClose={() => setFlash(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {flash ? <Alert severity="success" variant="filled" onClose={() => setFlash(null)}>{flash}</Alert> : undefined}
      </Snackbar>
      <Snackbar
        open={!!checkoutError}
        autoHideDuration={6000}
        onClose={() => setCheckoutError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {checkoutError ? (
          <Alert severity="error" onClose={() => setCheckoutError(null)}>
            {checkoutError}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
